import React from 'react';
import { isHolder, memoryPoolCapacity, requestStateLabel, resourceBytes, type Resource, type ResourceRequest } from '../utils/resourceCoordination';

export default function ResourceMemoryPool({ resource, requests, identity, priority }: {
  resource: Resource; requests: ResourceRequest[];
  identity: (request: ResourceRequest) => React.ReactNode;
  priority: (request: ResourceRequest) => React.ReactNode;
}) {
  const capacity = memoryPoolCapacity(resource, requests);
  if (!capacity) return null;
  const holders = requests.filter(isHolder);
  return <div className="resource-memory-pool card" aria-label="Memory pool allocations">
    <h3>Shared memory budget</h3>
    <div className="resource-pool-totals">
      <div><strong>{resourceBytes(capacity.total)}</strong><span>Total budget</span></div>
      <div><strong>{resourceBytes(capacity.allocated)}</strong><span>Allocated budget</span></div>
      <div><strong>{resourceBytes(capacity.available)}</strong><span>Unallocated budget</span></div>
    </div>
    <progress max={capacity.total} value={capacity.allocated} aria-label="Allocated memory budget" />
    <p className="hint">Several agents can hold allocations at once. This tracks cooperative budgets; actual RAM availability and OS limits are checked separately. Expired allocations remain charged until returned.</p>
    <h3>Current allocations <span className="resource-count">{holders.length}</span></h3>
    {!holders.length && <p className="hint">No current allocation. Agents request a quantity, verify physical availability, then explicitly claim it.</p>}
    {holders.map(request => <article className="resource-request" key={request.id}>
      <div className="resource-request-body"><strong>{request.summary}</strong>
        <p><b>{resourceBytes(request.amount_bytes ?? 0)}</b> · {requestStateLabel(request)}</p>
        {identity(request)}
        {request.state === 'yield_requested' && <p className="hint">Higher-priority work needs capacity. This allocation stays charged until the owner confirms a safe return.</p>}
      </div>{priority(request)}
    </article>)}
  </div>;
}
